// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Authentication utility functions
 */

/**
 * Result of the SSO self-provision call. Populated by `checkAuthentication`
 * for the AuthenticatedRoute to consume if it needs to redirect a
 * pending-activation advisor.
 */
export type AdvisorStatus = 'active' | 'pending_activation' | 'inactive' | 'unknown';

let lastSelfProvisionStatus: AdvisorStatus = 'unknown';

export function getLastAdvisorStatus(): AdvisorStatus {
  return lastSelfProvisionStatus;
}

/**
 * Call /api/sso/provision-self. Idempotent on the server side; for an
 * already-provisioned advisor this just touches `last_login_at` and returns
 * 200. For first-time SSO sign-ins, this creates the advisor row.
 *
 * Returns false on auth failure (401), true otherwise (including
 * pending_activation — the caller is expected to check
 * {@link getLastAdvisorStatus} to drive the routing decision).
 */
const provisionSelf = async (): Promise<boolean> => {
  try {
    const response = await fetch('/api/sso/provision-self', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.status === 401) {
      lastSelfProvisionStatus = 'unknown';
      return false;
    }
    if (response.status === 400) {
      // Brokerage has no SSO provider configured (password-only / dev tenant).
      // Skip JIT provisioning and let Step 2 (/api/advisors/me) decide auth.
      console.warn('provision-self returned 400 — likely no SSO config, skipping JIT');
      lastSelfProvisionStatus = 'unknown';
      return true;
    }
    if (!response.ok) {
      // 500 / etc. — log and assume not authenticated.
      console.warn('provision-self returned', response.status);
      lastSelfProvisionStatus = 'unknown';
      return false;
    }

    const body = await response.json();
    const status = String(body.status ?? '').toLowerCase();
    if (status === 'active' || status === 'pending_activation'
        || status === 'inactive') {
      lastSelfProvisionStatus = status as AdvisorStatus;
    } else {
      lastSelfProvisionStatus = 'unknown';
    }
    return true;
  } catch (error) {
    console.error('provision-self call failed', error);
    lastSelfProvisionStatus = 'unknown';
    return false;
  }
};

/**
 * Check if user is authenticated. Two-step:
 * 1. Call /api/sso/provision-self — idempotent for existing advisors, JIT
 *    for first-time SSO sign-ins. Returns the advisor's current status.
 * 2. Fall back to /api/advisors/me as a sanity check.
 *
 * The provision-self path is added in Phase 1.5 so SSO advisors who don't
 * yet have an `advisors` row don't bounce back to /login.
 */
export const checkAuthentication = async (): Promise<boolean> => {
  // Step 1: ensure the advisor row exists (or JIT it now).
  const provisioned = await provisionSelf();
  if (!provisioned) {
    return false;
  }

  // Step 2: sanity-check by hitting /me. This also keeps backwards
  // compatibility with password-only flows where provision-self returns
  // pending_activation but /me has a useful payload.
  try {
    const response = await fetch('/api/advisors/me', {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      return true;
    }

    if (response.status === 401) {
      return false;
    }

    // 403 or 404 from /me when provision-self ALREADY confirmed the
    // advisor's status means the calling JWT lacks the role the
    // endpoint needs. The most common case: the advisor was just
    // activated / JIT'd, the Keycloak user has the role NOW, but the
    // JWT in the user's session was minted before the role was
    // assigned and still doesn't carry ROLE_USER. Force a refresh-
    // token grant — the new access token will carry the role — and
    // retry /me once.
    if ((response.status === 403 || response.status === 404)
        && (lastSelfProvisionStatus === 'pending_activation'
            || lastSelfProvisionStatus === 'active'
            || lastSelfProvisionStatus === 'inactive')) {
      const refreshed = await forceTokenRefreshAndRetryMe();
      if (refreshed !== null) {
        return refreshed;
      }
      // Forced refresh didn't help (likely pending_activation — the
      // role really isn't on the Keycloak user yet). Trust the
      // provision-self status so AuthenticatedRoute can redirect to
      // /awaiting-approval; without this, the user gets bounced to
      // /login → provision-self succeeds → /me 403 → /login again,
      // infinite loop.
      return true;
    }

    // Handle redirects or HTML responses (server-side redirect to login)
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('text/html')) {
      return false;
    }

    return false;
  } catch (error) {
    console.error('Error checking authentication:', error);
    return false;
  }
};

/**
 * Force the advisor-bff to mint a fresh access token via the
 * refresh-token grant, then retry {@code /api/advisors/me}. Returns:
 *  - {@code true} if /me now returns 200 (the new JWT carries the role)
 *  - {@code false} if /me 401s after the refresh (session truly dead)
 *  - {@code null} if the refresh itself failed OR /me still 403/404s
 *    (caller falls back to trust-provision-self semantics)
 *
 * Re-entrant safe: the inner {@code _meRetryGuard} prevents this
 * function from triggering itself if the retry call were to ever
 * recurse (it shouldn't — we hit /api/advisors/me directly, not
 * checkAuthentication).
 */
let _meRetryGuard = false;
const forceTokenRefreshAndRetryMe = async (): Promise<boolean | null> => {
  if (_meRetryGuard) return null;
  _meRetryGuard = true;
  try {
    const refresh = await fetch('/api/auth/refresh?force=true', {
      method: 'GET',
      credentials: 'include',
    });
    if (!refresh.ok) {
      console.warn('Forced token refresh returned', refresh.status);
      return null;
    }

    const retry = await fetch('/api/advisors/me', {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (retry.ok) {
      return true;
    }
    if (retry.status === 401) {
      return false;
    }
    // 403/404 again — the role isn't on the Keycloak user yet (most
    // likely pending_activation). Let the caller fall through to the
    // trust-provision-self branch.
    return null;
  } catch (e) {
    console.warn('Forced token refresh failed', e);
    return null;
  } finally {
    _meRetryGuard = false;
  }
};

/**
 * Redirect to login page
 */
export const redirectToLogin = (): void => {
  globalThis.location.href = '/login';
};

/**
 * Extract tenant/subdomain from hostname
 * Used to identify which brokerage this deployment belongs to
 */
export const getTenant = (): string => {
  const hostname = globalThis.location.hostname;
  const parts = hostname.split('.');

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'tb1'; // Default tenant for local development
  }

  if (parts.length >= 3 && parts[1] === 'advisor') {
    return parts[0]; // Extract tenant from {tenant}.advisor.loa.com
  }

  if (parts[0] !== 'advisor') {
    return parts[0]; // Extract tenant from {tenant}.localhost.com
  }

  return 'tb1'; // Fallback to default tenant
};
